<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { MdEditor } from 'md-editor-v3'
import 'md-editor-v3/lib/style.css'

const props = withDefaults(
  defineProps<{
    /** 마크다운 내용 (v-model) */
    modelValue?: string
    /** 플레이스홀더 텍스트 */
    placeholder?: string
    /** 비활성화 상태 */
    disabled?: boolean
    /** 읽기 전용 상태 */
    readonly?: boolean
    /** 에디터 높이 */
    height?: string | number
    /** 테마 (light/dark) - 설정하지 않으면 자동으로 다크모드 감지 */
    theme?: 'light' | 'dark'
    /** 추가 CSS 클래스 */
    class?: string
  }>(),
  {
    modelValue: '',
    placeholder: '마크다운을 입력하세요...',
    disabled: false,
    readonly: false,
    height: '300px',
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'change': [value: string]
  'save': [value: string]
}>()

// 다크모드 상태
const isDarkMode = ref(false)

// 다크모드 감지 함수
const detectDarkMode = () => {
  isDarkMode.value = document.documentElement.classList.contains('dark')
}

// 현재 테마 계산 (props.theme이 있으면 그것 사용, 없으면 자동 감지)
const currentTheme = computed(() => {
  if (props.theme) {
    return props.theme
  }
  return isDarkMode.value ? 'dark' : 'light'
})

// MutationObserver로 다크모드 변경 감지
let darkModeObserver: MutationObserver | null = null

onMounted(() => {
  // 초기 다크모드 상태 감지
  detectDarkMode()
  
  // MutationObserver로 class 변경 감지
  darkModeObserver = new MutationObserver(() => {
    detectDarkMode()
  })
  
  darkModeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  })
})

onUnmounted(() => {
  if (darkModeObserver) {
    darkModeObserver.disconnect()
  }
})

const handleChange = (value: string) => {
  emit('update:modelValue', value)
  emit('change', value)
}

const handleSave = (value: string) => {
  emit('save', value)
}
</script>

<template>
  <div :class="['j-editor-wrapper', props.class]">
    <MdEditor
      :model-value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :read-only="readonly"
      :editor-class-name="'j-editor'"
      :preview-class-name="'j-editor-preview'"
      :style="{ height: typeof height === 'number' ? `${height}px` : height }"
      :theme="currentTheme"
      language="en-US"
      @update:model-value="handleChange"
      @on-save="handleSave"
    />
  </div>
</template>

<style scoped>
.j-editor-wrapper {
  @apply w-full h-full;
}
</style>
